import type { EditMapCommandMessage } from "@workadventure/messages";
import type { GameMapFrontWrapper } from "../../GameMap/GameMapFrontWrapper";
import type { GameScene } from "../../GameScene";
import type { MapEditorModeManager } from "../MapEditorModeManager";
import { DEPTH_MAP_EDITOR_AREAS_INDEX } from "../../DepthIndexes";
import { pendingBuildingFootprintStore } from "../../../../Stores/BuildingEditorStore";
import { MapEditorTool } from "./MapEditorTool";

/**
 * DR building constructor tool (task #81 Track 3b). Drag a rectangle → a
 * tile-snapped footprint ghost; on release the tile rectangle is handed to the
 * BuildingEditor side panel (via pendingBuildingFootprintStore) which picks
 * materials and confirms. Buildings are COMPILER INPUT: the confirm POSTs a
 * record and the real building appears on the next map rebuild — this tool
 * therefore only draws a footprint outline and never emits a WAM command.
 *
 * Pointer handlers bind in activate() and unbind in clear() so they do not
 * leak across tool switches (equipTool clears the previous tool then activates
 * the next).
 */
export class BuildingEditorTool extends MapEditorTool {
  private scene: GameScene;
  private footprintPreview: Phaser.GameObjects.Graphics;
  private drawing = false;
  private startWorld?: { x: number; y: number };

  private readonly onPointerDown: (pointer: Phaser.Input.Pointer) => void;
  private readonly onPointerMove: (pointer: Phaser.Input.Pointer) => void;
  private readonly onPointerUp: (pointer: Phaser.Input.Pointer) => void;

  constructor(mapEditorModeManager: MapEditorModeManager) {
    super();
    this.scene = mapEditorModeManager.getScene();
    this.footprintPreview = this.scene.add.graphics().setDepth(DEPTH_MAP_EDITOR_AREAS_INDEX);
    this.onPointerDown = (pointer) => this.handleDown(pointer);
    this.onPointerMove = (pointer) => this.handleMove(pointer);
    this.onPointerUp = (pointer) => this.handleUp(pointer);
  }

  public update(time: number, dt: number): void {
    // Nothing to animate.
  }

  public activate(): void {
    this.scene.input.on("pointerdown", this.onPointerDown);
    this.scene.input.on("pointermove", this.onPointerMove);
    this.scene.input.on("pointerup", this.onPointerUp);
  }

  public clear(): void {
    this.scene.input.off("pointerdown", this.onPointerDown);
    this.scene.input.off("pointermove", this.onPointerMove);
    this.scene.input.off("pointerup", this.onPointerUp);
    this.drawing = false;
    this.startWorld = undefined;
    this.footprintPreview.clear();
    this.scene.markDirty();
  }

  public destroy(): void {
    this.clear();
    this.footprintPreview.destroy();
  }

  public subscribeToGameMapFrontWrapperEvents(gameMapFrontWrapper: GameMapFrontWrapper): void {
    // Nothing to be done.
  }

  public handleKeyDownEvent(event: KeyboardEvent): void {
    // Nothing to be done.
  }

  public handleIncomingCommandMessage(editMapCommandMessage: EditMapCommandMessage): Promise<void> {
    // Buildings are compiler input, not WAM entities — no incoming edit commands.
    return Promise.resolve();
  }

  private handleDown(pointer: Phaser.Input.Pointer): void {
    if (pointer.rightButtonDown()) {
      return;
    }
    this.drawing = true;
    this.startWorld = { x: pointer.worldX, y: pointer.worldY };
  }

  private handleMove(pointer: Phaser.Input.Pointer): void {
    if (!this.drawing || !this.startWorld) {
      return;
    }
    const rect = this.snappedRect(pointer);
    this.footprintPreview.clear();
    this.footprintPreview.fillStyle(0x00ff88, 0.35);
    this.footprintPreview.fillRect(rect.x, rect.y, rect.width, rect.height);
    this.footprintPreview.lineStyle(2, 0x00ff88, 0.9);
    this.footprintPreview.strokeRect(rect.x, rect.y, rect.width, rect.height);
    this.scene.markDirty();
  }

  private handleUp(pointer: Phaser.Input.Pointer): void {
    if (!this.drawing || !this.startWorld) {
      return;
    }
    const rect = this.snappedRect(pointer);
    this.drawing = false;
    // Keep the ghost drawn while the panel collects materials.
    if (rect.width >= 32 && rect.height >= 32) {
      pendingBuildingFootprintStore.set({
        x: Math.round(rect.x / 32),
        y: Math.round(rect.y / 32),
        width: Math.round(rect.width / 32),
        depth: Math.round(rect.height / 32),
      });
    }
  }

  /** Tile-snapped rectangle in world px — buildings are always tile placements. */
  private snappedRect(pointer: Phaser.Input.Pointer): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    const sx = this.startWorld!.x;
    const sy = this.startWorld!.y;
    const width = Math.abs(pointer.worldX - sx);
    const height = Math.abs(pointer.worldY - sy);
    const x = Math.min(sx, pointer.worldX);
    const y = Math.min(sy, pointer.worldY);
    return {
      x: Math.floor(x / 32) * 32,
      y: Math.floor(y / 32) * 32,
      width: Math.floor(width / 32) * 32 + 32,
      height: Math.floor(height / 32) * 32 + 32,
    };
  }
}
